import { useTranslation } from "react-i18next";

import { MoneyText } from "@/components/MoneyText";
import type { CartLine } from "@/lib/types";

export interface LineDetail {
  itemDetail: string;
  serial: string;
}

/**
 * The lengths pos-service accepts (`LineDetailIn`: 商品情報詳細 ≤ 80, シリアル ≤ 20 characters — the
 * 購入記録情報 fields). T13-06 found the screen allowing 200 / 64: a long card name made the
 * default 商品情報詳細 too long and 「免税で会計へ進む」 failed with "入力に足りないところがあります"
 * and no field marked; the screen now keeps both inside the limits.
 */
export const ITEM_DETAIL_MAX = 80;
export const SERIAL_MAX = 20;

/** 商品情報詳細 defaults: the card's name + its Rank / grade; the serial = the slab's cert. */
export function defaultDetail(line: CartLine): LineDetail {
  const name = line.title.ja ?? line.title.en ?? "";
  const grade = line.grading?.company
    ? `${line.grading.company} ${line.grading.grade ?? ""}`.trim()
    : line.rawRank
      ? `Rank ${line.rawRank}`
      : "";
  // the grade stays when the name is long: name cut so that " / PSA 10" still fits
  const suffix = grade ? ` / ${grade}` : "";
  return {
    itemDetail: `${name.slice(0, Math.max(0, ITEM_DETAIL_MAX - suffix.length))}${suffix}`
      .trim()
      .slice(0, ITEM_DETAIL_MAX),
    serial: (line.grading?.certNumber ?? "").slice(0, SERIAL_MAX),
  };
}

/**
 * ≥ ¥1,000,000 per item (design/05 §3.3): 商品情報詳細 + シリアル are part of the 購入記録情報 —
 * filled in from the card (name + grade, the cert of the slab); the cashier checks / completes.
 */
export function HighValueDetail({
  lines,
  details,
  onChange,
}: {
  lines: CartLine[];
  details: Readonly<Record<string, LineDetail>>;
  onChange: (lineId: string, detail: LineDetail) => void;
}) {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="high-value"
      className="flex flex-col gap-2 rounded-md border border-status-pending p-3"
    >
      <h3 id="high-value" className="font-semibold">
        {t("taxfree.high_value_title")}
      </h3>
      <p className="text-sm text-muted-foreground">{t("taxfree.high_value_help")}</p>
      {lines.map((line) => {
        const detail = details[line.id] ?? defaultDetail(line);
        return (
          <div key={line.id} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-sm">{t("taxfree.item_detail")}</span>
              <input
                value={detail.itemDetail}
                maxLength={ITEM_DETAIL_MAX}
                onChange={(e) =>
                  onChange(line.id, {
                    ...detail,
                    itemDetail: e.target.value.slice(0, ITEM_DETAIL_MAX),
                  })
                }
                aria-invalid={!detail.itemDetail.trim()}
                className="min-h-12 rounded-md border px-3 aria-[invalid=true]:border-status-problem"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm">{t("taxfree.serial")}</span>
              <input
                value={detail.serial}
                maxLength={SERIAL_MAX}
                onChange={(e) =>
                  onChange(line.id, { ...detail, serial: e.target.value.slice(0, SERIAL_MAX) })
                }
                aria-invalid={!detail.serial.trim()}
                className="min-h-12 rounded-md border px-3 font-mono aria-[invalid=true]:border-status-problem"
              />
            </label>
            <MoneyText amount={line.lineTotal} />
          </div>
        );
      })}
    </section>
  );
}
